import {
  landFilterDefaults,
  landFilterFields,
  ROUTE_DOCS_LANDS,
  ROUTE_DOCS_MONITORING,
  ROUTE_DOCS_PROJECTS,
} from '@/common/constants';
import { IRegion } from '@/common/models/IStore';
import useLandStore from '@/features/lands/store/useLandStore';
import { useMonitoringStore } from '@/features/monitoring/store/useMonitoringStore';
import useProjectStore from '@/features/projects/store/useProjectStore';
import useStore from '@/lib/store';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FilterFormValue, filterSchema } from './filterSchema';

export default function useFilter({ page }: { page: string }) {
  const { status, district_id, region_id, type, lang } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { fetchLands } = useLandStore();
  const { fetchProjectList } = useProjectStore();
  const { fetchMonitoringFarmers } = useMonitoringStore();
  const defaultValues = useMemo(() => {
    const values: Record<string, any> = { ...landFilterDefaults };

    landFilterFields.forEach((field) => {
      if (field === 'project_action_id') {
        const arr = searchParams.getAll(field);
        if (arr.length) values[field] = arr;
        return;
      }
      const param = searchParams.get(field);
      if (param !== null) {
        values[field] = param;
      }
    });

    return values;
  }, [searchParams]);

  const form = useForm<FilterFormValue>({
    resolver: zodResolver(filterSchema),
    defaultValues,
    mode: 'onSubmit',
  });

  const { control, watch, handleSubmit, reset, setValue } = form;
  const regionId = watch('region_id');
  const { user, region, district, fetchDistrict, fetchRegion, plants } =
    useStore();

  useEffect(() => {
    fetchRegion();
  }, []);

  useEffect(() => {
    if (regionId) {
      fetchDistrict({
        region_id: String(
          region?.find((item) => item.code === Number(regionId))?.id,
        ),
      });
      setValue('district_id', '');
    }
  }, [regionId, setValue]);

  useEffect(() => {
    if (region_id) {
      fetchDistrict({ region_id });
    }
  }, [region_id]);

  const region_soato = useMemo(() => {
    return region?.find((item) => item.id === region_id)?.code;
  }, [region_id, region]);

  const district_soato = useMemo(() => {
    return district?.find((item) => item.id === district_id)?.code;
  }, [district_id, district]);

  const sizeParam = Number(searchParams.get('size')) || 20;
  const pageParam = Number(searchParams.get('page')) || 1;
  const regionIdParam = searchParams.get('region_id');
  const districtIdParam = searchParams.get('district_id');
  const farmerParam = searchParams.get('farmer');
  const innParam = Number(searchParams.get('inn')) || undefined;
  const contourParam = searchParams.get('contour_number');
  const cadastreParam = searchParams.get('cadastre');
  const regNumberParam = searchParams.get('reg_number');
  const areaFromParam = searchParams.get('area_from');
  const areaToParam = searchParams.get('area_to');
  const cropIdParam = searchParams.get('crop_id');
  const plant_id =
    page === ROUTE_DOCS_LANDS ? undefined : searchParams.get('plant_id') || '1';
  const year = searchParams.get('year') || '2026';

  const monthParam = searchParams.get('project_month');
  const actionIdParam = useMemo(
    () => searchParams.getAll('project_action_id'),
    [searchParams],
  );
  const statusParam = searchParams.get('project_status');

  const isSelectedValue = (v?: string | null) => !!v && v !== 'all';
  const isSelectedArray = (v?: string[] | null) =>
    Array.isArray(v) && v.length > 0;

  const query = useMemo(() => {
    const baseQuery = {
      size: sizeParam,
      page: pageParam,
      region_soato:
        page === ROUTE_DOCS_MONITORING
          ? region_soato
          : regionIdParam || undefined,
      district_soato:
        page === ROUTE_DOCS_MONITORING
          ? district_soato
          : districtIdParam || undefined,
      farmer: farmerParam || undefined,
      inn: innParam,
      contour_number: contourParam || undefined,
      cadastre: cadastreParam || undefined,
      reg_number: regNumberParam || undefined,
      area_from: areaFromParam || undefined,
      area_to: areaToParam || undefined,
      crop_id: cropIdParam || undefined,
      plant_id: plant_id || undefined,
      year,
    };

    if (page === ROUTE_DOCS_MONITORING) {
      return {
        ...baseQuery,
        month: isSelectedValue(monthParam) ? monthParam : undefined,
        action_id: isSelectedArray(actionIdParam) ? actionIdParam : undefined,
        status: isSelectedValue(statusParam) ? statusParam : undefined,
      };
    }

    return baseQuery;
  }, [
    page,
    region_soato,
    district_soato,
    sizeParam,
    pageParam,
    regionIdParam,
    districtIdParam,
    farmerParam,
    innParam,
    contourParam,
    cadastreParam,
    regNumberParam,
    areaFromParam,
    areaToParam,
    cropIdParam,
    plant_id,
    year,
    monthParam,
    actionIdParam,
    statusParam,
  ]);

  useEffect(() => {
    if (page === ROUTE_DOCS_LANDS) {
      fetchLands({ status, query });
    } else if (page === ROUTE_DOCS_PROJECTS) {
      fetchProjectList({ query });
    } else {
      if (region_id && !region_soato) return;
      if (district_id && !district_soato) return;
      fetchMonitoringFarmers({ query });
    }
  }, [status, query, page]);

  const regionList = useMemo(() => region?.filter((item) => item.id), [region]);
  const districtList = useMemo(
    () => district?.filter((item) => item.id),
    [district],
  );

  const plantsList = useMemo(
    () =>
      plants?.map((item) => ({
        value: String(item.id),
        label: item.name,
      })),
    [plants],
  );

  const selectFormat = useCallback(
    (item: IRegion) => ({
      value: String(item.code),
      label: item.name_uz,
    }),
    [],
  );

  const removeEmptyValues = (obj: Record<string, any>): Record<string, any> => {
    return Object.fromEntries(
      Object.entries(obj).filter(
        ([_, value]) =>
          value !== null &&
          value !== undefined &&
          value !== '' &&
          value !== 'all' &&
          !(Array.isArray(value) && value.length === 0),
      ),
    );
  };

  const monitoringFilterFields = [
    'project_month',
    'project_action_id',
    'project_status',
    'inn',
  ];

  const clearFilter = () => {
    reset(landFilterDefaults);
    if (page === ROUTE_DOCS_MONITORING) {
      setSearchParams((prev) => {
        monitoringFilterFields.forEach((field) => prev.delete(field));
        prev.delete('page');
        return prev;
      });
    } else {
      setSearchParams((prev) => {
        landFilterFields.forEach((field) => prev.delete(field));
        return prev;
      });
    }
  };

  const onSubmit = (values: FilterFormValue) => {
    if (page === ROUTE_DOCS_MONITORING) {
      const {
        project_month,
        project_action_id,
        project_status,
        inn: innVal,
      } = values;
      const hasInn = !!innVal?.trim();

      if (!hasInn) {
        let hasError = false;

        if (!isSelectedValue(project_month)) {
          form.setError('project_month', {
            type: 'manual',
            message: 'Maydonni tanlang',
          });
          hasError = true;
        }
        if (!isSelectedArray(project_action_id)) {
          form.setError('project_action_id', {
            type: 'manual',
            message: 'Maydonni tanlang',
          });
          hasError = true;
        }
        if (!isSelectedValue(project_status)) {
          form.setError('project_status', {
            type: 'manual',
            message: 'Maydonni tanlang',
          });
          hasError = true;
        }

        if (hasError) return;
      }

      const params = removeEmptyValues(values);
      const newSearchParams = new URLSearchParams(searchParams);
      monitoringFilterFields.forEach((field) => {
        newSearchParams.delete(field);
        const value = params[field];
        if (Array.isArray(value)) {
          value.forEach((v) => newSearchParams.append(field, v));
        } else if (value) {
          newSearchParams.set(field, value);
        }
      });
      newSearchParams.delete('page');
      newSearchParams.delete('month');

      navigate({
        pathname: `/${lang}/${ROUTE_DOCS_MONITORING}/${type}/${region_id}/${district_id}`,
        search: newSearchParams.toString(),
      });
      return;
    }

    const params = removeEmptyValues(values);
    setSearchParams((prev) => {
      landFilterFields.forEach((field) => {
        if (params[field]) prev.set(field, params[field]);
        else prev.delete(field);
      });
      return prev;
    });
  };

  return {
    form,
    control,
    user,
    regionList,
    districtList,
    selectFormat,
    clearFilter,
    plantsList,
    handleForm: handleSubmit(onSubmit),
  };
}
